import {
  AssemblyType,
  AssemblyTypeValue,
  InstallationStatus,
  JobFormDataBackflow,
  TestRepair,
  TestResult,
  TestResultAvb,
  TestResultDc,
  TestResultRp,
  TestResultSvbPvb,
  TestResultType2Bypass,
} from '../../types/jobFormData';
import { isTestable, meterReadingRequired } from './helpers';
import {
  avbAirGapTestAttemptSchema,
  dcTestAttemptSchema,
  repairSchema,
  rpTestAttemptSchema,
  svPvTestAttemptSchema,
  type2BypassTestAttemptSchema,
} from './schema';
import { ObjectSchema, ValidationError } from 'yup';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export type ValidationResult = {
  valid: boolean;
  errors: string[];
};

export const validateBackflowTest = (
  testData: JobFormDataBackflow
): ValidationResult => {
  testData = testData || {};
  let errors: string[] = [];

  // comments?: string,
  // improperlyInstalled?: OnOff,
  // installationStatus?: 1 | 2 | 3 | 4, // 1: new, 2: existing, 3: removed, 4: replaced
  // repairsRequired?: OnOff,
  // unableToTest?: OnOff,
  // meterReading?: number, // decimal

  // shutoff1Position?: OnOff,
  // shutoff2Position?: OnOff,
  // shutoffMainPosition?: OnOff

  if (testData.installationStatus !== InstallationStatus.REMOVED) {
    if (!testData.backflowAssembly) {
      errors.push('Assembly information is required');
    } else if (!testData.backflowAssembly.type) {
      errors.push('Assembly type is required');
    }
  }

  // if the assemblyMakeName or assemblyModelName are empty or unset, then we ignore this check. If either is unknown, throw error
  if (
    testData.backflowAssembly?.modelName?.toString().toUpperCase() ===
      'UNKNOWN' ||
    testData.backflowAssembly?.makeName?.toString().toUpperCase() === 'UNKNOWN'
  ) {
    errors.push('Assembly make and/or model cannot be "Unknown"');
  }

  if (!testData.certificationNumber) {
    errors.push('Tester certification number is required');
  }

  if (isTestable(testData)) {
    if (!testData.gaugeId && !testData.gaugeNumber) {
      errors.push('Gauge information is required');
    } else if (!testData.gaugeCalibrated) {
      errors.push('Gauge calibration date is required');
    }

    const tests = testData.tests;
    if (tests && tests.length > 0 && testData.backflowAssembly?.type) {
      tests.forEach((testAttempt) => {
        if (
          testData.gaugeCalibrated &&
          testAttempt.testDate &&
          testAttempt.type !== 'repair'
        ) {
          const gaugeCalibrated = dayjs(
            testData.gaugeCalibrated,
            'YYYY-MM-DD',
            true
          );
          if (!gaugeCalibrated.isValid()) {
            errors.push(
              `Gauge calibration date "${testData.gaugeCalibrated}" is invalid`
            );
          } else {
            let testDate = dayjs(testAttempt.testDate, 'MM/DD/YYYY', true);
            if (!testDate.isValid()) {
              testDate = dayjs(testAttempt.testDate, 'M/D/YYYY', true);
            }
            if (!testDate.isValid()) {
              errors.push(`Test date "${testAttempt.testDate}" is invalid`);
            } else {
              const calibrationExpires = gaugeCalibrated.add(1, 'year');
              if (testDate.isAfter(calibrationExpires)) {
                errors.push(
                  `Gauge calibration expired on ${calibrationExpires.format(
                    'M/D/YYYY'
                  )} before the test date ${testDate.format('M/D/YYYY')}`
                );
              }
            }
          }
        }

        errors = errors.concat(
          validateTestResult(
            testAttempt,
            testData.backflowAssembly?.type as AssemblyTypeValue
          )
        );
      });

      if (
        meterReadingRequired(testData.backflowAssembly?.type) &&
        !testData.meterReading
      ) {
        errors.push('Meter reading required');
      }
    } else {
      errors.push('Test results are required');
    }
  } else {
    if (!testData.comments) {
      errors.push('Comments are required');
    }
  }

  return {
    errors,
    valid: errors.length === 0,
  };
};

export const validateTestResult = (
  testAttempt: TestResult | TestRepair,
  backflowAssemblyType: AssemblyTypeValue
): string[] => {
  if (testAttempt.type === 'repair') {
    return validateRepairs(testAttempt as TestRepair);
  }

  switch (backflowAssemblyType) {
    case AssemblyType.RP:
    case AssemblyType.RPDA:
    case AssemblyType.RPDA_2:
      return validateTestResultRp(testAttempt as TestResultRp);

    case AssemblyType.DC:
    case AssemblyType.DCDA:
    case AssemblyType.DCDA_2:
      return validateTestResultDc(testAttempt as TestResultDc);

    case AssemblyType.PVB:
    case AssemblyType.SVB:
      return validateTestResultSvbPvb(testAttempt as TestResultSvbPvb);

    case AssemblyType.AIR_GAP:
    case AssemblyType.AVB:
      return validateTestResultAvb(testAttempt as TestResultAvb);

    case AssemblyType.TYPE_2_BYPASS:
      return validateTestResultType2Bypass(
        testAttempt as TestResultType2Bypass
      );

    default:
      console.warn(
        `Assembly type ${backflowAssemblyType} is not yet supported`
      );
      return [];
  }
};

const validateSchema = (schema: ObjectSchema<any>, value: any): string[] => {
  try {
    schema.validateSync(value, {
      abortEarly: false,
    });
    return [];
  } catch (e) {
    if (e instanceof ValidationError) {
      return e.errors ?? e;
    }
    if (typeof e === 'string') {
      return [e];
    }
    if (e instanceof Error) {
      return [e.message];
    }
    return [];
  }
};

export const validateRepairs = (testAttempt: TestRepair) => {
  return validateSchema(repairSchema, testAttempt);
};

export const validateTestResultRp = (testAttempt: TestResultRp): string[] => {
  return validateSchema(rpTestAttemptSchema, testAttempt);
};

export const validateTestResultDc = (testAttempt: TestResultDc): string[] => {
  return validateSchema(dcTestAttemptSchema, testAttempt);
};

export const validateTestResultSvbPvb = (
  testAttempt: TestResultSvbPvb
): string[] => {
  return validateSchema(svPvTestAttemptSchema, testAttempt);
};

export const validateTestResultAvb = (testAttempt: TestResultAvb): string[] => {
  return validateSchema(avbAirGapTestAttemptSchema, testAttempt);
};

export const validateTestResultType2Bypass = (
  testAttempt: TestResultType2Bypass
): string[] => {
  return validateSchema(type2BypassTestAttemptSchema, testAttempt);
};
